var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2024 Sequent Tech <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { Suspense, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecordContext, useSidebarState } from "react-admin";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "@/providers/AuthContextProvider";
import ElectionHeader from "@/components/ElectionHeader";
import DashboardElection from "@/components/dashboard/election/Dashboard";
import MonitoringDashboardElection from "@/components/monitoring-dashboard/election/MonitoringDashboard";
import { Publish } from "../Publish/Publish";
import { EditElectionData } from "./ElectionData";
import { EPublishType } from "../Publish/EPublishType";
import { IPermissions } from "@/types/keycloak";
import { EditElectionEventUsers } from "../ElectionEvent/EditElectionEventUsers";
import { ResourceListStyles } from "@/components/styles/ResourceListStyles";
import { Box, Typography } from "@mui/material";
import { EElectionEventLockedDown } from "@sequentech/ui-core";
import { EditElectionEventApprovals } from "../ElectionEvent/EditElectionEventApprovals";
import { Tabs } from "@/components/Tabs";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
export var ElectionTabs = function () {
    var _a;
    var record = useRecordContext();
    var t = useTranslation().t;
    var _b = React.useState(uuidv4()), tabKey = _b[0], setTabKey = _b[1];
    var authContext = useContext(AuthContext);
    var usersPermissionLabels = authContext.permissionLabels;
    var _c = useState(true), hasPermissionToViewElection = _c[0], setHasPermissionToViewElection = _c[1];
    var open = useSidebarState()[0];
    var aliasRenderer = useAliasRenderer();
    var isElectionEventLocked = ((_a = record === null || record === void 0 ? void 0 : record.presentation) === null || _a === void 0 ? void 0 : _a.locked_down) == EElectionEventLockedDown.LOCKED_DOWN;
    var showDashboard = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_DASHBOARD_TAB);
    var showMonitoringDashboard = authContext.isAuthorized(true, authContext.tenantId, IPermissions.MONITORING_DASHBOARD_VIEW_ELECTION);
    var showData = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_DATA_TAB);
    var showVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_VOTERS_TAB);
    var showPublish = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_PUBLISH_TAB);
    var showApprovalsExecution = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_APPROVALS_TAB);
    useEffect(function () {
        if (usersPermissionLabels &&
            (record === null || record === void 0 ? void 0 : record.permission_label) &&
            !usersPermissionLabels.includes(record.permission_label)) {
            setHasPermissionToViewElection(false);
        }
        else {
            setHasPermissionToViewElection(true);
        }
    }, [record]);
    if (!hasPermissionToViewElection) {
        return (_jsx(ResourceListStyles.EmptyBox, { children: _jsx(Typography, __assign({ variant: "h4", paragraph: true }, { children: t("electionScreen.common.noPermission") })) }));
    }
    return (_jsxs(Box, __assign({ sx: { maxWidth: "calc(100vw - ".concat(open ? "352px" : "96px", ")"), bgcolor: "background.paper" }, className: "election-box" }, { children: [_jsx(ElectionHeader, { title: aliasRenderer(record), subtitle: "electionScreen.common.subtitle" }), _jsx(Tabs, { elements: __spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray([], (showDashboard
                    ? [
                        {
                            label: t("electionScreen.tabs.dashboard"),
                            component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Dashboard..." }) }, { children: _jsx(DashboardElection, {}) }))); },
                        },
                    ]
                    : []), true), (showMonitoringDashboard
                    ? [
                        {
                            label: t("electionScreen.tabs.monitoring"),
                            component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Dashboard..." }) }, { children: _jsx(MonitoringDashboardElection, {}) }))); },
                        },
                    ]
                    : []), true), (showData
                    ? [
                        {
                            label: t("electionScreen.tabs.data"),
                            component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Data..." }) }, { children: _jsx(EditElectionData, {}) }))); },
                        },
                    ]
                    : []), true), (showVoters
                    ? [
                        {
                            label: t("electionScreen.tabs.voters"),
                            component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Voters..." }) }, { children: _jsx(EditElectionEventUsers, { electionEventId: record === null || record === void 0 ? void 0 : record.election_event_id, electionId: record === null || record === void 0 ? void 0 : record.id }) }))); },
                        },
                    ]
                    : []), true), (showPublish
                    ? [
                        {
                            label: t("electionScreen.tabs.publish"),
                            component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Publish..." }) }, { children: _jsx(Publish, { electionEventId: record === null || record === void 0 ? void 0 : record.election_event_id, electionId: record === null || record === void 0 ? void 0 : record.id, type: EPublishType.Election }, tabKey) }))); },
                            action: function (index) {
                                localStorage.setItem("electionPublishTabIndex", index.toString());
                            },
                        },
                    ]
                    : []), true), (showApprovalsExecution
                    ? [
                        {
                            label: t("electionScreen.tabs.approvals"),
                            component: function () { return (_jsx(Suspense, __assign({ fallback: _jsx("div", { children: "Loading Approvals..." }) }, { children: _jsx(EditElectionEventApprovals, { electionEventId: record === null || record === void 0 ? void 0 : record.election_event_id, electionId: record === null || record === void 0 ? void 0 : record.id }) }))); },
                        },
                    ]
                    : []), true) })] })));
};
